'use client';

import type { CSSProperties } from 'react';
import { useTranslations } from 'next-intl';
import CustomLink from 'components/shared/CustomLink';
import { useMarketingBannerOffset } from 'components/shared/Header/MarketingBanner';
import { getSpeedsizeImageUrl } from 'components/shared/custom-image/CustomImage';
import { campaignHeroCtaProps } from './v3/homepageCta';
import v3 from './v3/v3.module.css';
import styles from './TwoHourDeliveryHero.module.css';

const DESKTOP_SRC = getSpeedsizeImageUrl(
  'web_v2/homepage-v3/banners/two-hour-delivery/desktop/two-hr-banner.png',
  1920,
);
const MOBILE_SRC = getSpeedsizeImageUrl(
  'web_v2/homepage-v3/banners/two-hour-delivery/mobile/two-hour-banner.png',
  800,
);

type TwoHourDeliveryHeroProps = {
  /** True when HeroCarousel already owns header clearance above this card. */
  stackedBelowCarousel?: boolean;
};

export const TwoHourDeliveryHero = ({
  stackedBelowCarousel = false,
}: TwoHourDeliveryHeroProps) => {
  const t = useTranslations('homepage');
  const bannerPx = useMarketingBannerOffset();
  const firstFold = !stackedBelowCarousel;

  return (
    <section
      className={`${styles.section} ${
        stackedBelowCarousel ? styles.stacked : ''
      }`}
      aria-label={t('homepage.twoHourHero.aria.section')}
      data-testid="two-hour-delivery-hero"
      data-header-theme="light"
      style={
        {
          '--campaign-banner-px': `${bannerPx}px`,
          '--frost-photo': `url("${MOBILE_SRC}")`,
        } as CSSProperties
      }
    >
      <picture>
        <source media="(max-width: 899px)" srcSet={MOBILE_SRC} />
        <img
          src={DESKTOP_SRC}
          alt=""
          className={styles.bg}
          fetchPriority={firstFold ? 'high' : undefined}
          loading={firstFold ? undefined : 'lazy'}
          decoding="async"
        />
      </picture>
      <div className={styles.overlay} aria-hidden />
      <div className={styles.bottomScrim} aria-hidden />
      <div className={styles.copy}>
        <div className={styles.titleGroup}>
          <p className={styles.eyebrow}>{t('homepage.twoHourHero.eyebrow')}</p>
          <h2 className={`${v3.campaignDisplay} ${styles.title}`}>
            {t('homepage.twoHourHero.titleLine1')}
            <br />
            {t('homepage.twoHourHero.titleLine2')}
          </h2>
          <p className={styles.subtitle}>{t('homepage.twoHourHero.subtitle')}</p>
        </div>
        <CustomLink
          href="/ring/buy/"
          className={`${v3.pill} ${styles.cta}`}
          {...campaignHeroCtaProps('ring')}
        >
          {t('homepage.cta.buyNow')}
        </CustomLink>
      </div>
    </section>
  );
};
